import type { Input } from './Input';

/**
 * On-screen touch controls for phones/tablets.
 * Left: virtual stick (advance / retreat). Right: combat + camera + pause.
 */
export class MobileControls {
  private root: HTMLElement;
  private stickBase: HTMLElement;
  private stickKnob: HTMLElement;
  private stickActive = false;
  private stickId: number | null = null;
  private holdCleanups: Array<() => void> = [];
  private visible = false;
  private enabled = true;

  constructor(
    private input: Input,
    private onPause?: () => void,
  ) {
    this.root = document.getElementById('mobile-pad')!;
    this.stickBase = document.getElementById('mob-stick')!;
    this.stickKnob = document.getElementById('mob-stick-knob')!;
    this.bindStick();
    this.bindActions();
    this.detect();
    window.addEventListener('resize', () => this.detect());
    window.addEventListener('orientationchange', () => this.detect());
  }

  /** Whether the device should use touch UI. */
  isTouchDevice(): boolean {
    return (
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(max-width: 900px)').matches ||
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0
    );
  }

  detect(): void {
    this.enabled = this.isTouchDevice();
    document.body.classList.toggle('is-touch', this.enabled);
    if (!this.enabled) this.hide();
  }

  show(): void {
    if (!this.enabled) return;
    this.visible = true;
    this.root.classList.remove('hidden');
    document.body.classList.add('touch-playing');
  }

  hide(): void {
    this.visible = false;
    this.root.classList.add('hidden');
    document.body.classList.remove('touch-playing');
    this.releaseStick();
    this.input.forceKey('KeyA', false);
    this.input.forceKey('KeyD', false);
  }

  get isVisible(): boolean {
    return this.visible;
  }

  private bindStick(): void {
    const base = this.stickBase;
    const onDown = (e: PointerEvent) => {
      if (!this.visible) return;
      e.preventDefault();
      e.stopPropagation();
      this.stickActive = true;
      this.stickId = e.pointerId;
      base.setPointerCapture?.(e.pointerId);
      this.moveStick(e);
    };
    const onMove = (e: PointerEvent) => {
      if (!this.stickActive || e.pointerId !== this.stickId) return;
      e.preventDefault();
      this.moveStick(e);
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== this.stickId) return;
      e.preventDefault();
      this.releaseStick();
    };
    base.addEventListener('pointerdown', onDown);
    base.addEventListener('pointermove', onMove);
    base.addEventListener('pointerup', onUp);
    base.addEventListener('pointercancel', onUp);
    base.addEventListener('lostpointercapture', () => this.releaseStick());
  }

  private moveStick(e: PointerEvent): void {
    const rect = this.stickBase.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let dx = e.clientX - cx;
    let dy = e.clientY - cy;
    const max = rect.width * 0.34;
    const len = Math.hypot(dx, dy) || 1;
    if (len > max) {
      dx = (dx / len) * max;
      dy = (dy / len) * max;
    }
    this.stickKnob.style.transform = `translate(${dx}px, ${dy}px)`;

    // Horizontal only for piste (vertical ignored)
    const nx = dx / max;
    const dead = 0.22;
    this.input.forceKey('KeyA', false);
    this.input.forceKey('KeyD', false);
    this.input.forceKey('ShiftLeft', false);
    if (nx < -dead) {
      this.input.forceKey('KeyA', true);
      if (nx < -0.72) this.input.forceKey('ShiftLeft', true);
    } else if (nx > dead) {
      this.input.forceKey('KeyD', true);
      if (nx > 0.72) this.input.forceKey('ShiftLeft', true);
    }
  }

  private releaseStick(): void {
    this.stickActive = false;
    this.stickId = null;
    this.stickKnob.style.transform = 'translate(0, 0)';
    this.input.forceKey('KeyA', false);
    this.input.forceKey('KeyD', false);
    this.input.forceKey('ShiftLeft', false);
  }

  private bindActions(): void {
    const hold = (id: string, code: string) => {
      const el = document.getElementById(id);
      if (!el) return;
      const down = (e: PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        el.setPointerCapture?.(e.pointerId);
        el.classList.add('active');
        this.input.forceKey(code, true);
      };
      const up = (e: PointerEvent) => {
        e.preventDefault();
        el.classList.remove('active');
        this.input.forceKey(code, false);
      };
      el.addEventListener('pointerdown', down);
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
      el.addEventListener('lostpointercapture', () => {
        el.classList.remove('active');
        this.input.forceKey(code, false);
      });
      this.holdCleanups.push(() => {
        el.removeEventListener('pointerdown', down);
        el.removeEventListener('pointerup', up);
      });
    };

    const tap = (id: string, code: string) => {
      const el = document.getElementById(id);
      if (!el) return;
      const down = (e: PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        el.classList.add('active');
        this.input.forceTap(code);
        window.setTimeout(() => el.classList.remove('active'), 120);
      };
      el.addEventListener('pointerdown', down);
      this.holdCleanups.push(() => el.removeEventListener('pointerdown', down));
    };

    // Combat (taps → just-pressed edge; parries also hold while finger down)
    tap('mob-lunge', 'Space');
    tap('mob-fleche', 'KeyF');
    hold('mob-parry-q', 'KeyQ');
    hold('mob-parry-e', 'KeyE');
    tap('mob-riposte', 'KeyR');
    tap('mob-cam', 'KeyC');

    const pauseBtn = document.getElementById('mob-pause');
    if (pauseBtn) {
      pauseBtn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.input.forceTap('Escape');
        this.onPause?.();
      });
    }
  }

  dispose(): void {
    for (const c of this.holdCleanups) c();
    this.hide();
  }
}
